import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { formatLocalDate, getWeekdayLabels } from './scheduleUtils';

// Month-view popover shared by the mobile Date field and the desktop "Other Dates" link.
// Only dates present in slotsByDate are selectable.
const ScheduleCalendar = ({ dates, slotsByDate, selectedDate, onSelect, spanishLanguagePref, className = '' }) => {
  const { t } = useTranslation();

  // The popover is only mounted while open, so this opens on the selected date's month each time.
  const [viewMonth, setViewMonth] = useState(() => moment(selectedDate || dates[0] || undefined));

  const calendarDays = useMemo(() => {
    const startDate = viewMonth.clone().startOf('month').startOf('week');
    const endDate = viewMonth.clone().endOf('month').endOf('week');

    const days = [];
    const day = startDate.clone();
    while (day.isBefore(endDate) || day.isSame(endDate, 'day')) {
      days.push(day.clone());
      day.add(1, 'day');
    }
    return days;
  }, [viewMonth]);

  // Month navigation stays within the months that actually have install slots.
  const canGoPrev = dates.length > 0 && viewMonth.isAfter(moment(dates[0]), 'month');
  const canGoNext = dates.length > 0 && viewMonth.isBefore(moment(dates[dates.length - 1]), 'month');

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    if (canGoPrev) {setViewMonth((prev) => prev.clone().subtract(1, 'month'));}
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    if (canGoNext) {setViewMonth((prev) => prev.clone().add(1, 'month'));}
  };

  return (
    <div className={`spc-mobile-popover spc-mobile-calendar-popover ${className}`}>
      <div className="spc-mobile-popover__label">{t('singlePageCheckout.selectDateLabel')}</div>
      <div className="spc-calendar-header">
        <button type="button" className="spc-calendar-nav" onClick={handlePrevMonth} disabled={!canGoPrev}>&laquo;</button>
        <span className="spc-calendar-month-title">{formatLocalDate(viewMonth, 'MMM YYYY', spanishLanguagePref)}</span>
        <button type="button" className="spc-calendar-nav" onClick={handleNextMonth} disabled={!canGoNext}>&raquo;</button>
      </div>

      <div className="spc-calendar-weekdays">
        {getWeekdayLabels(spanishLanguagePref).map((day) => <span key={day}>{day}</span>)}
      </div>

      <div className="spc-calendar-days-grid">
        {calendarDays.map((dayObj) => {
          const dateStr = dayObj.format('YYYY-MM-DD');
          const isSelected = dateStr === selectedDate;
          const isCurrentMonth = dayObj.isSame(viewMonth, 'month');
          const isAvailable = isCurrentMonth && Boolean(slotsByDate[dateStr]);
          // Full date for screen readers / hover, like the multi-step checkout's antd Calendar
          // cells (which carry a YYYY-MM-DD title) - the visible label is only the day number.
          const fullDateLabel = formatLocalDate(dayObj, 'dddd, MMMM D, YYYY', spanishLanguagePref);

          return (
            <button
              type="button"
              key={dateStr}
              disabled={!isAvailable}
              title={fullDateLabel}
              aria-label={fullDateLabel}
              aria-pressed={isSelected}
              className={`spc-calendar-day-btn ${isSelected ? 'is-selected' : ''} ${!isCurrentMonth ? 'is-other-month' : ''} ${isCurrentMonth && !isAvailable ? 'is-unavailable' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(dateStr);
              }}
            >
              {dayObj.date()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ScheduleCalendar;
